<template>
  <div style="padding-top: 3em;">
    <img src="/images/pae-logo.png" style="max-width: 140px; margin-bottom: 2em;" />

    <h4 class="title">Food Business Notification Form</h4>
    <p class="subtitle">As required under Section 86 of Food Act 2001</p>
    <p>Use the online form below to submit your Food Business Notification request.</p>
    <p><a target="_blank" href='https://www.cityofpae.sa.gov.au/?a=1619417'>PDF Version</a></p>
    <br />

    <div>
      <template v-if="message">
        <div class="notification is-primary">{{ message }}</div>
      </template>

      <form-wizard dusk="form-wizard" @submit.prevent="onSubmit" @on-complete="onSubmit" shape="tab" color="#17C4EE"
        title subtitle ref="wizard" v-if="!message">

        <input type="hidden" name="_token" :value="csrf" />
        <input type="hidden" v-model="form" />

        <div style="padding: 1em 0px; color: #aaa;">
          <small>* Required Fields</small>
        </div>

        <!-- *************************************** -->
        <!-- TAB #1 - PART 1 BUSINESS INFORMATION    -->
        <!-- *************************************** -->
        <tab-content title="Business Information" :before-change="validateStep1">

          <div class="columns is-multiline">
            <div class="column is-8">

              <div class="field">
                <label class="label">Please select one of the following *</label>

                <div class="control">
                  <label class="radio">
                    <input type="radio" id="submissionType-0" value="New Food Business" v-model="submissionType" />
                    New Food Business
                  </label>
                  <label class="radio">
                    <input type="radio" id="submissionType-1" value="Change of Ownership Only"
                      v-model="submissionType" />
                    Change of Ownership Only
                  </label>
                  <label class="radio">
                    <input type="radio" id="submissionType-2" value="Updating Details of Existing Food Business"
                      v-model="submissionType" />
                    Updating Details of Existing Food Business
                  </label>
                </div>
                <span v-if="$v.submissionType.$error && !$v.submissionType.required" class="help is-danger">Please
                  select one
                  of the following</span>
              </div>

            </div>
          </div>

          <div class="columns is-multiline">
            <div class="column is-8">
              <div class="field">
                <label class="label">ABN or ACN Number *</label>
                <div class="control">
                  <input autofocus type="text" placeholder="Please enter your ABN or ACN Number"
                    :class="{ 'input': true, 'is-danger': $v.abnacn.$error }" @input="$v.abnacn.$touch()"
                    v-model="abnacn" />
                  <span v-if="$v.abnacn.$error && !$v.abnacn.required" class="help is-danger">This field is
                    required</span>
                  <span v-if="$v.abnacn.$error && !$v.abnacn.AlphNumCharsValidator" class="help is-danger">Invalid
                    characters
                    entered</span>
                </div>
              </div>
            </div>

            <div class="column is-8">
              <div class="field">
                <label class="label">Registered Business Entity *</label>
                <label
                  style="font-size: .8rem; line-height: 1.1rem; border: 1px solid #333; padding: 8px; display: block; margin-bottom: 10px;">
                  <strong>IMPORTANT:</strong> Trusts are NOT an accepted business entity when submitting this
                  form.<br />
                  If the trustee of a Trust is specified as a proprietor pursuant to Section 37 of the Food Act a copy
                  of
                  the trust deed must be provided to City of Port Adelaide Enfield.</label>
                <div class="control">
                  <input autofocus type="text" placeholder="Please enter the name of the business entity"
                    :class="{ 'input': true, 'is-danger': $v.registeredBusinessEntity.$error }"
                    @input="$v.registeredBusinessEntity.$touch()" v-model="registeredBusinessEntity" />
                  <span v-if="$v.registeredBusinessEntity.$error && !$v.registeredBusinessEntity.required"
                    class="help is-danger">This field is required</span>
                  <span v-if="$v.registeredBusinessEntity.$error && !$v.registeredBusinessEntity.AlphNumCharsValidator"
                    class="help is-danger">Invalid characters entered</span>
                </div>


              </div>
            </div>

            <div class="column is-8">
              <div class="field">
                <label class="label">Trading or Business Name *</label>
                <div class="control">
                  <input autofocus type="text" placeholder="Please enter the trading or business name"
                    :class="{ 'input': true, 'is-danger': $v.tradingName.$error }" @input="$v.tradingName.$touch()"
                    v-model="tradingName" />
                  <span v-if="$v.tradingName.$error && !$v.tradingName.required" class="help is-danger">This field is
                    required</span>
                  <span v-if="$v.tradingName.$error && !$v.tradingName.AlphNumCharsValidator"
                    class="help is-danger">Invalid
                    characters entered</span>
                </div>
              </div>
            </div>

          </div>

          <div class="columns is-multiline">
            <!-- Enter Address Selector Here -->
            <div class="column is-8">
              <div class="field">
                <label class="label">
                  Business Address
                  <small class="has-text-weight-light">
                    <strong>(Select from suggested address in dropdown list)</strong>
                  </small> *
                </label>

                <div class="control">
                  <PropertyAddress fieldName="businessAddress" source="/api/address/search"
                    placeholder="eg. 1/10 Main St Semaphore" v-on:address="saveBusinessAddress"
                    v-on:addressTyping="saveBusinessAddressTyping"
                    v-on:addressPropertyNo="saveBusinessAddressPropertyNo">
                  </PropertyAddress>
                  <div v-if="businessAddressPropertyNo == 0 && businessAddressTyping.length > 0"
                    style="background: #eee; padding: 1em;">
                    <small>
                      Please type address in full and then select correct address from the drop down list.
                      <br />eg. 1/10 Main St Semaphore SA 5091
                    </small>
                    <template v-if="$v.businessAddressPropertyNo.$error || this.businessAddressNotFound == true">
                      (
                      <input type="checkbox" id="businessAddressNotFound" v-model="businessAddressNotFound" />
                      <label for="businessAddressNotFound">
                        <small>I cannot find my address in the dropdown list.</small>
                      </label> )
                    </template>
                  </div>
                  <span v-if="$v.businessAddressPropertyNo.$error" class="help is-danger">Valid address is required.
                    (Please
                    select from dropdown list as you type)</span>
                </div>
                <div class="notification is-danger" v-if="businessAddressPropertyNo == ''">
                  Property must be in the
                  <strong>City of Port Adelaide Enfield</strong> area
                </div>
              </div>
            </div>

            <div class="column is-8">
              <div class="field">
                <label class="label">Business Address Type *</label>

                <div class="control">
                  <label class="radio">
                    <input type="radio" id="businessAddressType-0" value="Fixed location commercial food business"
                      v-model="businessAddressType" />
                    Fixed location commercial food business.
                  </label><br />
                  <label class="radio">
                    <input type="radio" id="businessAddressType-1" value="Home based food business"
                      v-model="businessAddressType" />
                    Home based food business
                  </label><br />
                  <label class="radio">
                    <input type="radio" id="businessAddressType-2" value="Mobile food vehicle"
                      v-model="businessAddressType" />
                    Mobile food vehicle (garaged location)
                  </label><br />
                  <label class="radio">
                    <input type="radio" id="businessAddressType-3" value="Market stall operator"
                      v-model="businessAddressType" />
                    Market stall operator
                  </label>
                </div>

                <span v-if="$v.businessAddressType.$error && !$v.businessAddressType.required"
                  class="help is-danger">Please
                  select an option</span>

              </div>
            </div>



            <!-- ADDED NEW FIELD 22 NOV 2023 - REQUESTED BY BRAD PROSSER - TICKET # 88695 -->
            <div class="column is-8" v-if="businessAddressType == 'Mobile food vehicle'">
              <div class="field">
                <label class="label">Food Vehicle Registration Plate Number</label>
                <div class="control">
                  <input type="text" placeholder="Please enter food vehicle registration number"
                    :class="{ 'input': true, 'is-danger': $v.businessFoodVehicleRegNumber.$error }"
                    @input="$v.businessFoodVehicleRegNumber.$touch()" v-model="businessFoodVehicleRegNumber" />
                  <span v-if="$v.businessFoodVehicleRegNumber.$error && !$v.businessFoodVehicleRegNumber.required"
                    class="help is-danger">This field is required</span>
                  <span
                    v-if="$v.businessFoodVehicleRegNumber.$error && !$v.businessFoodVehicleRegNumber.AlphNumCharsValidator"
                    class="help is-danger">Invalid characters entered</span>
                </div>
              </div>
            </div>




            <div class="column is-8">
              <div class="field">
                <label class="label">
                  Is the postal address different to the business address? *
                </label>
                <div class="control">
                  <div class="select">
                    <select name="businessPostalAddressDifferent" v-model="businessPostalAddressDifferent">
                      <option :value="null">--Please select--</option>
                      <option>Yes</option>
                      <option>No</option>
                    </select>
                  </div>
                </div>
              </div>
            </div>


            <template v-if="businessPostalAddressDifferent == 'Yes'">
              <div class="column is-8">
                <div class="field">
                  <label class="label">Business Postal Address *</label>
                  <div class="control">
                    <input autofocus type="text" placeholder="Please enter postal address"
                      :class="{ 'input': true, 'is-danger': $v.businessPostalAddress.$error }"
                      @input="$v.businessPostalAddress.$touch()" v-model="businessPostalAddress" />
                    <span v-if="$v.businessPostalAddress.$error && !$v.businessPostalAddress.required"
                      class="help is-danger">This field is required</span>
                    <span v-if="$v.businessPostalAddress.$error && !$v.businessPostalAddress.AlphNumCharsValidator"
                      class="help is-danger">Invalid characters entered</span>
                  </div>
                </div>
              </div>
            </template>


          </div>

          <div class="columns is-multiline">

            <div class="column is-8">
              <div class="field">
                <label class="label">Business Contact Number *</label>
                <div class="control">
                  <input type="text" placeholder="Please enter business contact number (eg. 84051234)"
                    :class="{ 'input': true, 'is-danger': $v.businessContactNo.$error }"
                    @input="$v.businessContactNo.$touch()" v-model="businessContactNo" />
                  <span v-if="$v.businessContactNo.$error && !$v.businessContactNo.required"
                    class="help is-danger">Field is
                    required</span>
                  <span v-if="$v.businessContactNo.$error && !$v.businessContactNo.numeric" class="help is-danger">Only
                    numbers allowed (No Spaces or Dashes)</span>
                </div>
              </div>
            </div>

            <div class="column is-8">
              <div class="field">
                <label class="label">Business Email Address *</label>
                <div class="control">
                  <input type="text" placeholder="Please enter business email adress"
                    :class="{ 'input': true, 'is-danger': $v.businessEmail.$error }" v-model="businessEmail"
                    @blur="$v.businessEmail.$touch()" />
                  <span v-if="$v.businessEmail.$error && !$v.businessEmail.required" class="help is-danger">Field is
                    required</span>
                  <span v-if="$v.businessEmail.$error && !$v.businessEmail.email" class="help is-danger">Format is
                    incorrect</span>
                </div>
              </div>
            </div>

            <div class="column is-8">
              <div class="field">
                <label class="label">Number of Food Handlers *<br />
                  <small style="font-weight: 100;">Numbers of employees (full time equivalent) working directly with
                    food.</small></label>

                <div class="control">
                  <input type="text" placeholder="0"
                    :class="{ 'input': true, 'is-danger': $v.numberOfFoodHandlers.$error }"
                    @input="$v.numberOfFoodHandlers.$touch()" v-model="numberOfFoodHandlers" />
                  <span v-if="$v.numberOfFoodHandlers.$error && !$v.numberOfFoodHandlers.required"
                    class="help is-danger">Field is required</span>
                  <span v-if="$v.numberOfFoodHandlers.$error && !$v.numberOfFoodHandlers.numeric"
                    class="help is-danger">Only
                    numbers allowed (No Spaces or Dashes)</span>
                </div>
              </div>
            </div>

            <div class="column is-8">
              <div class="field">
                <label class="label">Date Business Commenced *</label>

                <div class="control" style="vertical-align: middle; display: inline-block;">
                  <datepicker :format="customFormatter" id="datepicker1"
                    :input-class="{ 'input': true, 'is-danger': $v.dateBusinessCommenced.$error }"
                    style="margin-right:1em; width: 110px;" placeholder="Date" v-model="dateBusinessCommenced">
                  </datepicker>
                </div>
              </div>
              <span v-if="$v.dateBusinessCommenced.$error && !$v.dateBusinessCommenced.required"
                class="help is-danger">Date
                is required</span>
            </div>

          </div>

        </tab-content>













        <!-- ****************************************** -->
        <!-- TAB #2 - PART 2 FOOD BUSINESS OPERATIONS   -->
        <!-- ****************************************** -->
        <tab-content title="Food Business Operations" :before-change="validateStep2">

          <div class="columns is-multiline">
            <div class="column is-8">
              <div class="field">
                <label class="label">Business Operation Type *</label>

                <div class="control">
                  <label class="radio">
                    <input type="radio" id="businessType-0" value="Retailer or Food Service" v-model="businessType" />
                    Retailer or Food Service
                  </label> <small>(i.e café, fast food, supermarket)</small><br />
                  <label class="radio">
                    <input type="radio" id="businessType-1" value="Processor or Manufacturing" v-model="businessType" />
                    Processor or Manufacturing
                  </label> <small>(i.e producing goods for other food businesses to sell to consumer)</small><br />
                  <label class="radio">
                    <input type="radio" id="businessType-2" value="Distribution, Storage or Transport"
                      v-model="businessType" />
                    Distribution, Storage or Transport
                  </label> <small>(i.e. warehouse storage and/or delivery of food to other businesses)</small><br />
                </div>

                <span v-if="$v.businessType.$error && !$v.businessType.required" class="help is-danger">Please select an
                  option</span>

              </div>
            </div>
          </div>



          <div class="columns is-multiline">
            <div class="column is-8">
              <div class="field">
                <label class="label">Please briefly describe the food business operations. *<br /><small
                    class="has-text-weight-light">(include types of food produced)</small></label>
                <div class="control">
                  <textarea class="textarea" :class="{ 'input': true, 'is-danger': $v.businessDescription.$error }"
                    @input="$v.businessDescription.$touch()" v-model="businessDescription"></textarea>
                  <span v-if="$v.businessDescription.$error && !$v.businessDescription.required"
                    class="help is-danger">Field
                    is required</span>
                  <span v-if="$v.businessDescription.$error && !$v.businessDescription.AlphNumCharsValidator"
                    class="help is-danger">Invalid format</span>
                </div>
              </div>
            </div>

            <div class="column is-8">
              <div class="field">
                <label class="label">Please briefly describe the business history *<br /><small
                    class="has-text-weight-light">(is the business a new fitout? Or has it previously been used as a
                    food
                    business?)</small></label>
                <div class="control">
                  <textarea class="textarea" :class="{ 'input': true, 'is-danger': $v.businessHistory.$error }"
                    @input="$v.businessHistory.$touch()" v-model="businessHistory"></textarea>
                  <span v-if="$v.businessHistory.$error && !$v.businessHistory.required" class="help is-danger">This
                    field is
                    required</span>
                  <span v-if="$v.businessHistory.$error && !$v.businessHistory.AlphNumCharsValidator"
                    class="help is-danger">Invalid format</span>
                </div>
              </div>
            </div>

            <div class="column is-8">
              <div class="field">
                <label class="label">Please briefly describe the business trading hours *</label>
                <div class="control">
                  <textarea class="textarea" :class="{ 'input': true, 'is-danger': $v.businessTradingHours.$error }"
                    @input="$v.businessTradingHours.$touch()" v-model="businessTradingHours"></textarea>
                  <span v-if="$v.businessTradingHours.$error && !$v.businessTradingHours.required"
                    class="help is-danger">Field is required</span>
                  <span v-if="$v.businessTradingHours.$error && !$v.businessTradingHours.AlphNumCharsValidator"
                    class="help is-danger">Invalid format</span>
                </div>
              </div>
            </div>
          </div>

        </tab-content>










        <!-- ********************************************* -->
        <!-- TAB #3 - PART 3 PROPRIETOR CONTACT DETAILS    -->
        <!-- ********************************************* -->
        <tab-content title="Proprietor Contact Details" :before-change="validateStep3">


          <div class="columns is-multiline">
            <div class="column is-8">
              <div class="field">
                <label class="label">Proprietor Name *</label>
                <div class="control">
                  <input autofocus type="text" placeholder="Please enter proprietor name"
                    :class="{ 'input': true, 'is-danger': $v.proprietorName.$error }"
                    @input="$v.proprietorName.$touch()" v-model="proprietorName" />
                  <span v-if="$v.proprietorName.$error && !$v.proprietorName.required" class="help is-danger">This field
                    is
                    required</span>
                  <span v-if="$v.proprietorName.$error && !$v.proprietorName.AlphNumCharsValidator"
                    class="help is-danger">Invalid characters entered</span>
                </div>
              </div>
            </div>


            <div class="column is-8">
              <div class="field">
                <label class="label">Preferred Language *</label>
                <div class="control">
                  <input autofocus type="text" placeholder="Please enter preferred language"
                    :class="{ 'input': true, 'is-danger': $v.proprietorPreferredLanguage.$error }"
                    @input="$v.proprietorPreferredLanguage.$touch()" v-model="proprietorPreferredLanguage" />
                  <span v-if="$v.proprietorPreferredLanguage.$error && !$v.proprietorPreferredLanguage.required"
                    class="help is-danger">This field is required</span>
                  <span
                    v-if="$v.proprietorPreferredLanguage.$error && !$v.proprietorPreferredLanguage.AlphNumCharsValidator"
                    class="help is-danger">Invalid characters entered</span>
                </div>
              </div>
            </div>


            <div class="column is-8">
              <div class="field">
                <label class="label">Proprietor Contact Number *</label>
                <div class="control">
                  <input type="text" placeholder="Please enter proprietor contact number (eg. 84051234)"
                    :class="{ 'input': true, 'is-danger': $v.proprietorContactNo.$error }"
                    @input="$v.proprietorContactNo.$touch()" v-model="proprietorContactNo" />
                  <span v-if="$v.proprietorContactNo.$error && !$v.proprietorContactNo.required"
                    class="help is-danger">Field
                    is required</span>
                  <span v-if="$v.proprietorContactNo.$error && !$v.proprietorContactNo.numeric"
                    class="help is-danger">Only
                    numbers allowed (No Spaces or Dashes)</span>
                </div>
              </div>
            </div>

            <div class="column is-8">
              <div class="field">
                <label class="label">Proprietor Email Address *</label>
                <div class="control">
                  <input type="text" placeholder="Please enter proprietor email adress"
                    :class="{ 'input': true, 'is-danger': $v.proprietorEmail.$error }" v-model="proprietorEmail"
                    @blur="$v.proprietorEmail.$touch()" />
                  <span v-if="$v.proprietorEmail.$error && !$v.proprietorEmail.required" class="help is-danger">Field is
                    required</span>
                  <span v-if="$v.proprietorEmail.$error && !$v.proprietorEmail.email" class="help is-danger">Format is
                    incorrect</span>
                </div>
              </div>
            </div>

          </div>


          <div class="columns is-multiline">
            <div class="column is-8">
              <div class="field">
                <label class="label">Proprietor Residential Address *</label>
                <div class="control">
                  <input autofocus type="text" placeholder="Please enter residential address"
                    :class="{ 'input': true, 'is-danger': $v.proprietorResidentialAddress.$error }"
                    @input="$v.proprietorResidentialAddress.$touch()" v-model="proprietorResidentialAddress" />
                  <span v-if="$v.proprietorResidentialAddress.$error && !$v.proprietorResidentialAddress.required"
                    class="help is-danger">This field is required</span>
                  <span
                    v-if="$v.proprietorResidentialAddress.$error && !$v.proprietorResidentialAddress.AlphNumCharsValidator"
                    class="help is-danger">Invalid characters entered</span>
                </div>
              </div>
            </div>
          </div>


          <div class="columns is-multiline">
            <div class="column is-8">
              <div class="field">
                <label class="label">Proprietor Postal Address *</label>
                <div class="control">
                  <input autofocus type="text" placeholder="Please enter postal address"
                    :class="{ 'input': true, 'is-danger': $v.proprietorPostalAddress.$error }"
                    @input="$v.proprietorPostalAddress.$touch()" v-model="proprietorPostalAddress" />
                  <span v-if="$v.proprietorPostalAddress.$error && !$v.proprietorPostalAddress.required"
                    class="help is-danger">This field is required</span>
                  <span v-if="$v.proprietorPostalAddress.$error && !$v.proprietorPostalAddress.AlphNumCharsValidator"
                    class="help is-danger">Invalid characters entered</span>
                </div>
              </div>
            </div>
          </div>

        </tab-content>





        <!-- ******************************************** -->
        <!-- TAB #4 - PART 4 OTHER GENERAL INFORMATION    -->
        <!-- ******************************************** -->

        <tab-content title="Other General Information" :before-change="validateStep4">

          <div class="columns is-multiline">

            <div class="column is-8">
              <div class="field">
                <label class="label">
                  Do you have previous experience within the commercial Food Industry? *
                </label>
                <div class="control">
                  <div class="select" :class="{ 'is-danger': $v.foodIndustryExperience.$error }">
                    <select name="foodIndustryExperience" v-model="foodIndustryExperience"
                      :class="{ 'is-danger': $v.foodIndustryExperience.$error }"
                      @blur="$v.foodIndustryExperience.$touch()">
                      <option :value="null">--Please select--</option>
                      <option>Yes</option>
                      <option>No</option>
                    </select>
                  </div>
                  <span v-if="$v.foodIndustryExperience.$error && !$v.foodIndustryExperience.required"
                    class="help is-danger">Please select an option</span>
                </div>
              </div>
            </div>

            <template v-if="foodIndustryExperience == 'Yes'">

              <div class="column is-8">
                <div class="field">
                  <label class="label">Please briefly describe your industry experience *</label>
                  <div class="control">
                    <textarea class="textarea"
                      :class="{ 'input': true, 'is-danger': $v.foodIndustryExperienceDescription.$error }"
                      @input="$v.foodIndustryExperienceDescription.$touch()"
                      v-model="foodIndustryExperienceDescription"></textarea>
                    <span
                      v-if="$v.foodIndustryExperienceDescription.$error && !$v.foodIndustryExperienceDescription.required"
                      class="help is-danger">Field is required</span>
                    <span
                      v-if="$v.foodIndustryExperienceDescription.$error && !$v.foodIndustryExperienceDescription.AlphNumCharsValidator"
                      class="help is-danger">Invalid format</span>
                  </div>
                </div>
              </div>

            </template>

          </div>




          <div class="columns is-multiline">

            <div class="column is-8">
              <div class="field">
                <label class="label">
                  Have you completed any training related to food safety and hygiene? *
                </label>
                <div class="control">
                  <div class="select" :class="{ 'is-danger': $v.foodTrainingCompleted.$error }">
                    <select name="foodTrainingCompleted" v-model="foodTrainingCompleted"
                      :class="{ 'is-danger': $v.foodTrainingCompleted.$error }"
                      @blur="$v.foodTrainingCompleted.$touch()">
                      <option :value="null">--Please select--</option>
                      <option>Yes</option>
                      <option>No</option>
                    </select>
                  </div>
                  <span v-if="$v.foodTrainingCompleted.$error && !$v.foodTrainingCompleted.required"
                    class="help is-danger">Please select an option</span>
                </div>
              </div>
            </div>

            <template v-if="foodTrainingCompleted == 'Yes'">

              <div class="column is-8">
                <div class="field">
                  <label class="label">Please provide a brief explanation *</label>
                  <div class="control">
                    <textarea class="textarea"
                      :class="{ 'input': true, 'is-danger': $v.foodTrainingCompletedDescription.$error }"
                      @input="$v.foodTrainingCompletedDescription.$touch()"
                      v-model="foodTrainingCompletedDescription"></textarea>
                    <span
                      v-if="$v.foodTrainingCompletedDescription.$error && !$v.foodTrainingCompletedDescription.required"
                      class="help is-danger">Field is required</span>
                    <span
                      v-if="$v.foodTrainingCompletedDescription.$error && !$v.foodTrainingCompletedDescription.AlphNumCharsValidator"
                      class="help is-danger">Invalid format</span>
                  </div>
                </div>
              </div>

            </template>

          </div>






          <div class="columns is-multiline">
            <div class="column is-8">
              <fieldset style="border: 1px solid #333; padding: 1rem;">

                <legend class="label" style="padding:.6rem;">Please upload additional information (optional)</legend>
                <FileDragDropInstant v-on:fileIDs="appendFileIDs" v-on:removeFileID="removeFileID">
                </FileDragDropInstant>

                <strong style="margin-bottom: .5rem;">Additional Info Attached to this form:</strong>
                <div class="form-check">


                  <input type="checkbox" class="form-check-input" id="menu" value="menu" v-model="attachmentList">
                  <label for="menu">Menu</label>
                  <br />
                  <input type="checkbox" class="form-check-input" id="certificate" value="certificate"
                    v-model="attachmentList">
                  <label for="certificate">Certificates of Food Safety Training</label>
                  <br />
                  <input type="checkbox" class="form-check-input" id="trust" value="trust" v-model="attachmentList">
                  <label for="trust">Copy of Trust Deed</label>
                  <br />

                  <input type="checkbox" class="form-check-input" id="other" value="other" v-model="attachmentList">
                  <label for="other">Other</label>

                </div>

              </fieldset>

            </div>



            <div class="column is-8">
              <div class="notification is-warning" style="margin-bottom: 0px;">
                <p style="font-weight: 900;">I hereby certify that the included information is accurate and I wish to
                  formally
                  submit this Food Business Notification form as required under Section 86 of the Food Act 2001.</p>
                <br />
                <div class="field">
                  <label class="label">Name of Person Submitting Form *</label>
                  <div class="control">
                    <input autofocus type="text" placeholder="Please enter name"
                      :class="{ 'input': true, 'is-danger': $v.nameOfPersonCompletedForm.$error }"
                      @input="$v.nameOfPersonCompletedForm.$touch()" v-model="nameOfPersonCompletedForm" />
                    <span v-if="$v.nameOfPersonCompletedForm.$error && !$v.nameOfPersonCompletedForm.required"
                      class="help is-danger">This field is required</span>
                    <span
                      v-if="$v.nameOfPersonCompletedForm.$error && !$v.nameOfPersonCompletedForm.AlphNumCharsValidator"
                      class="help is-danger">Invalid characters entered</span>
                  </div>
                </div>

                <div class="form-check">
                  <label class="form-check-label" for="agree">
                    <input type="checkbox" class="form-check-input" name="agree" id="agree" value="Yes" :checked="agree"
                      @click="agree = !agree" />
                    <strong>I Agree</strong>
                  </label>
                </div>
              </div>

              <span v-if="$v.agree.$error && !$v.agree.required" class="help is-danger" style="margin-bottom: 2rem;">You
                must
                agree before submitting.</span>


            </div>

          </div>
        </tab-content>


        <div class="column is-8" v-if="enableGoogleCaptch">
          <div class="field">
            <vue-recaptcha ref="invisibleRecaptcha" @verify="onVerify" @expired="onExpired" size="invisible"
              :sitekey="googleCaptchaKey"></vue-recaptcha>
          </div>
        </div>

        <div class="modal" v-bind:class="{ 'is-active': loading }">
          <div class="modal-background"></div>
          <div class="modal-content has-text-centered">
            <h1 class="title is-3 has-text-light">Please wait...</h1>
            <button class="button is-info is-loading is-fullwidth is-large"
              style="font-size: 5em; background: transparent;">Submitting</button>
          </div>
        </div>





      </form-wizard>

    </div>
  </div>
</template>

<script>
import VueRecaptcha from "vue-recaptcha";
import datepicker from "vuejs-datepicker";

import PropertyAddress from "../../../../../../resources/assets/js/components/address/Typeahead-CouncilAddress.vue";
import FileDragDropInstant from "../../../../../../Modules/FileManagement/Resources/assets/js/components/FileDragDropInstant.vue";

import {
  alpha,
  required,
  requiredIf,
  email,
  numeric,
  minValue,
  minLength
} from "vuelidate/lib/validators";
import { validationMixin } from "vuelidate";
import moment from "moment";

import { FormWizard, TabContent } from "vue-form-wizard";
import "vue-form-wizard/dist/vue-form-wizard.min.css";

// Custom Validators
import { AlphNumCharsValidator } from "../../../../../../resources/assets/js/validators/alpha-num-chars-validator";

export default {
  components: {
    PropertyAddress,
    VueRecaptcha,
    FormWizard,
    TabContent,
    FileDragDropInstant,
    datepicker
  },

  props: {
    form: {}
  },

  data() {
    return {
      googleCaptchaKey: "6LcyrE0UAAAAAExxoj3NBm1vA-WMu88CIigyzkBU",
      enableGoogleCaptch: false,

      csrf: "",
      formTitle: this.form,
      message: "",
      submitted: false,
      loading: false,

      fileIDs: [],

      submissionType: [],
      abnacn: "",
      registeredBusinessEntity: "",
      tradingName: "",
      businessAddressPropertyNo: "",
      businessAddress: "",
      businessAddressTyping: "",
      businessAddressNotFound: false,
      businessAddressType: "",
      businessFoodVehicleRegNumber: "",
      businessPostalAddressDifferent: "No",
      businessPostalAddress: "",
      businessContactNo: "",
      businessEmail: "",
      numberOfFoodHandlers: 0,
      dateBusinessCommenced: "",
      businessType: "",
      businessDescription: "",
      businessHistory: "",
      businessTradingHours: "",

      proprietorName: "",
      proprietorResidentialAddress: "",
      proprietorPostalAddress: "",
      proprietorContactNo: "",
      proprietorEmail: "",
      proprietorPreferredLanguage: "",

      foodIndustryExperience: "No",
      foodIndustryExperienceDescription: "",
      foodTrainingCompleted: "No",
      foodTrainingCompletedDescription: "",

      attachmentList: [],

      // Date options
      format: "dd/MM/yyyy",
      disabled: {
        from: new Date(
          new Date().getFullYear(),
          new Date().getMonth(),
          new Date().getDate()
        )
      },
      disabledEnd: {
        from: new Date(
          new Date().getFullYear(),
          new Date().getMonth(),
          new Date().getDate()
        )
      },

      nameOfPersonCompletedForm: "",

      agree: false
    };
  },

  validations: {
    submissionType: { required },
    abnacn: { required, AlphNumCharsValidator },

    registeredBusinessEntity: { required, AlphNumCharsValidator },
    tradingName: { required, AlphNumCharsValidator },
    businessAddressPropertyNo: {
      required: requiredIf(function () {
        return this.businessAddressNotFound == false;
      })
    },
    businessAddressType: { required },

    businessFoodVehicleRegNumber: {
      required: requiredIf(function () {
        return this.businessAddressType == "Mobile food vehicle";
      }),
      AlphNumCharsValidator
    },

    businessPostalAddress: {
      required: requiredIf(function () {
        return this.businessPostalAddressDifferent == "Yes";
      }),
      AlphNumCharsValidator
    },

    businessContactNo: { required, numeric },
    businessEmail: { required, email },
    numberOfFoodHandlers: { required, numeric },
    dateBusinessCommenced: { required },

    formStep1: [
      "submissionType",
      "abnacn",
      "registeredBusinessEntity",
      "tradingName",
      "businessAddressPropertyNo",
      "businessAddressType",
      "businessFoodVehicleRegNumber",
      "businessPostalAddress",
      "businessContactNo",
      "businessEmail",
      "numberOfFoodHandlers",
      "dateBusinessCommenced"
    ],

    businessType: { required },
    businessDescription: { required, AlphNumCharsValidator },
    businessHistory: { required, AlphNumCharsValidator },
    businessTradingHours: { required, AlphNumCharsValidator },

    formStep2: [
      "businessType",
      "businessDescription",
      "businessHistory",
      "businessTradingHours"
    ],

    proprietorName: { required },
    proprietorResidentialAddress: { required, AlphNumCharsValidator },
    proprietorPostalAddress: { required, AlphNumCharsValidator },

    proprietorContactNo: { required, numeric },
    proprietorEmail: { required, email },
    proprietorPreferredLanguage: { required, AlphNumCharsValidator },

    formStep3: [
      "proprietorName",
      "proprietorResidentialAddress",
      "proprietorPostalAddress",
      "proprietorContactNo",
      "proprietorEmail",
      "proprietorPreferredLanguage"
    ],

    foodIndustryExperience: { required },
    foodIndustryExperienceDescription: {
      required: requiredIf(function () {
        return this.foodIndustryExperience == "Yes";
      }),
      AlphNumCharsValidator
    },
    foodTrainingCompleted: { required },
    foodTrainingCompletedDescription: {
      required: requiredIf(function () {
        return this.foodTrainingCompleted == "Yes";
      }),
      AlphNumCharsValidator
    },

    nameOfPersonCompletedForm: { required, AlphNumCharsValidator },
    agree: { required },

    formStep4: [
      "foodIndustryExperience",
      "foodIndustryExperienceDescription",
      "foodTrainingCompleted",
      "foodTrainingCompletedDescription",
      "nameOfPersonCompletedForm",
      "agree"
    ]
  },

  computed: {
    fullName: function () {
      // return this.firstname + " " + this.lastname;
      return this.proprietorName;
    },
    precis: function () {
      return (
        "Food Notification - " +
        "(" +
        this.tradingName +
        ") " +
        (this.businessAddressPropertyNo > 0
          ? this.businessAddress
          : this.businessAddressTyping)
      );
    },
    formData: function () {
      var fields = {};
      fields.fileIDs = this.fileIDs;

      fields.emailAddress = this.businessEmail;
      fields.contactNumber = this.businessContactNo;

      fields.submissionType = this.submissionType;
      fields.abnacn = this.abnacn;
      fields.registeredBusinessEntity = this.registeredBusinessEntity;
      fields.tradingName = this.tradingName;

      fields.address =
        this.businessAddressPropertyNo > 0
          ? this.businessAddress
          : this.businessAddressTyping;
      fields.propertyNo =
        this.businessAddressPropertyNo > 0
          ? this.businessAddressPropertyNo
          : 0;

      fields.addressCustomer = this.proprietorAddress;

      fields.businessAddress = this.businessAddress;
      fields.businessAddressType = this.businessAddressType;
      fields.businessFoodVehicleRegNumber = this.businessFoodVehicleRegNumber;
      fields.businessPostalAddressDifferent = this.businessPostalAddressDifferent;
      fields.businessPostalAddress = this.businessPostalAddress;

      fields.businessContactNo = this.businessContactNo;

      fields.businessEmail = this.businessEmail;
      fields.numberOfFoodHandlers = this.numberOfFoodHandlers;
      fields.dateBusinessCommenced = this.dateBusinessCommenced;
      fields.businessType = this.businessType;
      fields.businessDescription = this.businessDescription;
      fields.businessHistory = this.businessHistory;
      fields.businessTradingHours = this.businessTradingHours;

      fields.proprietorName = this.proprietorName;
      fields.proprietorResidentialAddress = this.proprietorResidentialAddress;
      fields.proprietorPostalAddress = this.proprietorPostalAddress;
      fields.proprietorContactNo = this.proprietorContactNo;
      fields.proprietorEmail = this.proprietorEmail;
      fields.proprietorPreferredLanguage = this.proprietorPreferredLanguage;

      fields.nameOfPersonCompletedForm = this.nameOfPersonCompletedForm;

      fields.foodIndustryExperience = this.foodIndustryExperience;
      fields.foodIndustryExperienceDescription = this.foodIndustryExperienceDescription;
      fields.foodTrainingCompleted = this.foodTrainingCompleted;
      fields.foodTrainingCompletedDescription = this.foodTrainingCompletedDescription;

      fields.attachmentList = this.attachmentList;

      fields.precis = this.precis;
      fields.subject = "11.71.2.12";

      var actions = [];
      actions.push("A11.71.2.45");
      fields.action = actions;

      return JSON.stringify(fields);
    }
  },

  methods: {
    scrollToTop() {
      window.scrollTo(0, 1100);
    },

    onComplete: function () {
      alert("yay!");
    },

    validate() {
      this.$v.form.$touch();
      var isValid = !this.$v.form.$invalid;
      this.$emit("on-validate", this.$data, isValid);
      return isValid;
    },

    // FILE ATTACHMENT HANDLING
    saveFileIDs(value) {
      this.fileIDs = value;
    },
    appendFileIDs(value) {
      let self = this;
      value.forEach(function (v) {
        if (self.fileIDs.indexOf(v) < 0) {
          self.fileIDs.push(v);
        }
      });
    },
    removeFileID(value) {
      this.fileIDs.splice(this.fileIDs.indexOf(value), 1);
    },

    saveBusinessAddress(value) {
      this.businessAddress = value;
    },
    saveBusinessAddressTyping(value) {
      console.log(value);
      this.businessAddressTyping = value;
    },
    saveBusinessAddressPropertyNo(value) {
      this.businessAddressPropertyNo = value;
    },

    saveProprietorAddress(value) {
      this.proprietorAddress = value;
    },

    // STEP VALIDATION
    validateStep1() {
      this.$v.formStep1.$touch();
      this.scrollToTop();
      return !this.$v.formStep1.$invalid;
    },
    validateStep2() {
      this.$v.formStep2.$touch();
      this.scrollToTop();
      return !this.$v.formStep2.$invalid;
    },
    validateStep3() {
      this.$v.formStep3.$touch();
      this.scrollToTop();
      return !this.$v.formStep3.$invalid;
    },
    validateStep4() {
      this.$v.formStep4.$touch();
      return !this.$v.formStep4.$invalid;
    },

    onSubmit() {
      if (this.validateStep4()) {
        if (this.enableGoogleCaptch) {
          this.$refs.invisibleRecaptcha.execute();
        } else {
          this.submitForm();
        }
      }
    },

    submitForm() {
      this.loading = true;
      axios
        .post("/onlineform/" + this.form, {
          _token: this.csrf,
          formTitle: this.formTitle,
          fullName: this.proprietorName,
          emailAddress: this.businessEmail,
          contactNumber: this.businessContactNo,
          precis: this.precis,
          formData: this.formData,
          message: ""
        })
        .then(response => {
          //console.log(response.data.message)
          this.message = response.data.message;
          this.clear();
          //this.submitted = true
          setTimeout(() => (this.submitted = true), 5000);
          this.loading = false;
        });
    },

    onVerify: function (response) {
      if (response) {
        axios
          .post("/api/recaptchaSiteVerify", {
            value: response
          })
          .then(gresponse => {
            //console.log(gresponse)

            if (gresponse["data"] === "valid") {
              this.submitForm();
            } else {
              //this.loading = false
              this.message = "Failed verify google captcha!";
              console.log("Failed verify google captcha!");
            }
          })
          .catch(error => {
            console.log(error.response.data);
          });
      } else {
        alert("Failed captcha");
      }
    },
    onExpired: function () {
      console.log("Expired");
    },

    onSuccess(response) { },

    clear() { },

    // DATE PICKER
    customFormatter(date) {
      return moment(date).format("DD/MM/YYYY");
    }
  },

  mounted() {
    this.csrf = window.laravel.csrfToken;
  }
};
</script>



// WEBPACK FOOTER //
// Modules/OnlineForm/Resources/assets/js/components/LayoutFoodBusinessNotification.vue